import { useQueries } from "@tanstack/react-query";
import { DEFAULT_PERSONA, PERSONAS } from "@macroscope/shared";
import type { Persona } from "@macroscope/shared";
import { usePersona } from "../lib/prefs";
import { fetchMarketSnapshot, fetchCalendar, fetchPositioning, fetchLiquidity, fetchGeo } from "../lib/api";
import { computeMacroWeather } from "../lib/weather";
import { Link } from "wouter";
import { PersonaSelector } from "../components/PersonaSelector";
import { HeadlineInstruments } from "../components/HeadlineInstruments";
import { MacroWeatherChip } from "../components/MacroWeather";
import { RiskOnOff } from "../components/RiskOnOff";
import { WhatToWatch } from "../components/WhatToWatch";

export default function Dashboard() {
  const [persona, setPersona] = usePersona();

  const results = useQueries({
    queries: [
      { queryKey: ["markets"], queryFn: fetchMarketSnapshot },
      { queryKey: ["calendar"], queryFn: fetchCalendar },
      { queryKey: ["positioning"], queryFn: fetchPositioning },
      { queryKey: ["liquidity"], queryFn: fetchLiquidity },
      { queryKey: ["geo"], queryFn: fetchGeo },
    ],
  });

  const [markets, calendar, positioning, liquidity, geo] = results;
  const allLoaded = results.every((r) => r.isSuccess);

  const weather = allLoaded
    ? computeMacroWeather(persona, {
        quotes: markets.data?.quotes ?? [],
        events: calendar.data?.events ?? [],
        positions: positioning.data?.positions ?? [],
        liquidity: liquidity.data,
        trackers: geo.data?.trackers,
      })
    : undefined;

  const currentPersonaLabel = PERSONAS.find((p) => p.id === persona)?.label ?? DEFAULT_PERSONA;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-400">
            <span>Dashboard</span>
            <span className="text-slate-600">/</span>
            <span>{currentPersonaLabel}</span>
          </div>
          <div className="flex items-center gap-3">
            <PersonaSelector value={persona} onChange={(p) => setPersona(p as Persona)} />
            <RiskOnOff snapshot={markets.data} liquidity={liquidity.data} geo={geo.data} />
          </div>
        </div>
        <MacroWeatherChip weather={weather} />
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-medium uppercase tracking-wider text-slate-400">Headline instruments</h2>
        <HeadlineInstruments snapshot={markets.data} />
      </section>

      <section>
        <Link
          href="/brief"
          className="flex items-center justify-between rounded-xl border border-macro-accent/20 bg-macro-accent/10 p-5 transition-colors hover:bg-macro-accent/15"
        >
          <div>
            <h2 className="text-sm font-semibold text-macro-accent">Open today’s Session Brief →</h2>
            <p className="mt-1 text-xs text-slate-400">
              Engine notes, model reads, and a ranked watchlist for {currentPersonaLabel}.
            </p>
          </div>
          <span className="rounded-full bg-macro-accent px-3 py-1 text-xs font-semibold text-white">New</span>
        </Link>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
            <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">What to watch</h2>
            <WhatToWatch calendar={calendar.data} geo={geo.data} positioning={positioning.data} />
          </div>
        </div>
        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
          <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Liquidity</h2>
          {liquidity.data ? (
            <div className="space-y-3">
              <div className="font-mono text-2xl font-semibold">${(liquidity.data.net_liq / 1000).toFixed(1)}T</div>
              <div className="text-sm text-slate-400">US net liquidity proxy</div>
              <div className="text-xs text-slate-500">
                {liquidity.data.chg_4w > 0
                  ? "Liquidity expanding — generally supportive for risk."
                  : liquidity.data.chg_4w < 0
                    ? "Liquidity contracting — headwind for risk."
                    : "Liquidity unchanged — no strong push."}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">4w change</span>
                  <span className={liquidity.data.chg_4w >= 0 ? "text-macro-up" : "text-macro-down"}>
                    ${(liquidity.data.chg_4w / 1000).toFixed(1)}B
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">13w change</span>
                  <span className={liquidity.data.chg_13w >= 0 ? "text-macro-up" : "text-macro-down"}>
                    ${(liquidity.data.chg_13w / 1000).toFixed(1)}B
                  </span>
                </div>
              </div>
              <div className="text-[10px] text-slate-600">
                Source: {liquidity.data.source} · {new Date(liquidity.data.updated_at).toLocaleString()} · delayed
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">Loading liquidity…</div>
          )}
        </div>
      </section>
    </div>
  );
}
